import { ComponentType } from 'react'

import { DataCollectionCategories } from 'modules/processes/domain'
import { TaskTypes } from 'modules/processes/hooks/types'
import { TaskConfigurationTypes } from 'modules/processes/lib/taskConfigurationTypes'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'

import { useFeaturesProvider } from 'modules/processes/components/ItemWizard/Providers/features'
import RequestContractSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/ContractData/Selector'
import RequestBankAccountSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/RequestBankAccount/Selector'
import RequestEmployeeAdditionalInformation from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/RequestEmployeeAdditionalInformation/Selector'
import RequestGeneralInformationSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/RequestGeneralInformation/Selector'
import RequestIdSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/RequestId/Selector'
import SendDocumentSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/SendDocument/Selector'
import SignDocumentsSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/SignDocuments/Selector'
import SingleTaskSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/Single/Selector'
import UploadDocumentSelector from 'modules/processes/components/ItemWizard/Tasks/SuperTasks/UploadDocument/Selector'

const OFFBOARDING_TASKS: TaskTypes[] = [
  TaskConfigurationTypes.Single,
  TaskConfigurationTypes.UploadDocument,
  TaskConfigurationTypes.SignDocuments,
  TaskConfigurationTypes.SendDocumentTemplate,
]

const TRAINING_TASKS: TaskTypes[] = [
  TaskConfigurationTypes.Single,
  TaskConfigurationTypes.UploadDocument,
  TaskConfigurationTypes.SignDocuments,
  TaskConfigurationTypes.SendDocumentTemplate,
]

export type TaskComponent = ComponentType<{
  onSelect: (taskType: TaskTypes) => void
}>

export const useTaskSelectorComponents: () => Partial<Record<TaskTypes, TaskComponent>> = () => {
  const { featureAvailabilities } = useFeaturesProvider()
  const { process } = useProcessContext()
  const isOffboarding = process?.category === 'offboarding'
  const isTraining = process?.category === 'training'

  const SUPER_TASKS: Partial<Record<TaskConfigurationTypes, TaskComponent>> = {
    [TaskConfigurationTypes.Single]: SingleTaskSelector,
    [TaskConfigurationTypes.UploadDocument]: UploadDocumentSelector,
    ...(!featureAvailabilities.documents_templates?.available && {
      [TaskConfigurationTypes.SignDocuments]: SignDocumentsSelector,
    }),
    ...(featureAvailabilities.documents_templates?.available && {
      [TaskConfigurationTypes.SendDocumentTemplate]: SendDocumentSelector,
    }),
  }

  const DATA_COLLECTION_SUPER_TASKS: Partial<Record<DataCollectionCategories, TaskComponent>> = {
    [DataCollectionCategories.identificationNumber]: RequestIdSelector,
    [DataCollectionCategories.bankInformation]: RequestBankAccountSelector,
    [DataCollectionCategories.generalInformation]: RequestGeneralInformationSelector,
    [DataCollectionCategories.employee_questions_cf]: RequestEmployeeAdditionalInformation,
    [DataCollectionCategories.contract_data]: RequestContractSelector,
  }

  const TASKS_TYPES = Object.assign({}, SUPER_TASKS, DATA_COLLECTION_SUPER_TASKS)

  if (isOffboarding) {
    return Object.fromEntries(
      OFFBOARDING_TASKS.filter((taskType) => taskType in TASKS_TYPES).map((taskType) => [
        taskType,
        TASKS_TYPES[taskType],
      ])
    )
  }

  if (isTraining) {
    return Object.fromEntries(
      TRAINING_TASKS.filter((taskType) => taskType in TASKS_TYPES).map((taskType) => [
        taskType,
        TASKS_TYPES[taskType],
      ])
    )
  }

  return TASKS_TYPES
}

export const taskSelectorOrder = () => {
  return [
    TaskConfigurationTypes.Single,
    DataCollectionCategories.generalInformation,
    DataCollectionCategories.employee_questions_cf,
    DataCollectionCategories.identificationNumber,
    DataCollectionCategories.bankInformation,
    DataCollectionCategories.contract_data,
    TaskConfigurationTypes.UploadDocument,
    TaskConfigurationTypes.SignDocuments,
    TaskConfigurationTypes.SendDocumentTemplate,
  ]
}
